export const GOOGLE_TRANSLATE_STORAGE_KEY = "google_translate_language";
export const GOOGLE_TRANSLATE_PAGE_LANGUAGE = "en";
export const GOOGLE_TRANSLATE_PENDING_KEY = "google_translate_pending";
export const GOOGLE_TRANSLATE_STATUS_EVENT = "google_translate_status";

const GOOGLE_TRANSLATE_SCRIPT_ID = "google-translate-script";
const GOOGLE_TRANSLATE_COOKIE_NAME = "googtrans";
const SUPPORTED_THREE_LETTER_CODES = new Set([
  "ceb",
  "co",
  "eo",
  "fy",
  "haw",
  "hmn",
  "jw",
  "ku",
  "la",
  "lb",
  "mg",
  "mi",
  "mn",
  "my",
  "ny",
  "sm",
  "sn",
  "st",
  "su",
  "tt",
  "ug",
  "yi",
  "yo",
  "zu",
]);

const GOOGLE_LANGUAGE_CODE_MAP = {
  afrikaans: "af",
  albanian: "sq",
  amharic: "am",
  arabic: "ar",
  armenian: "hy",
  azerbaijani: "az",
  basque: "eu",
  belarusian: "be",
  bengali: "bn",
  bosnian: "bs",
  bulgarian: "bg",
  catalan: "ca",
  cebuano: "ceb",
  chinese: "zh-CN",
  corsican: "co",
  croatian: "hr",
  czech: "cs",
  danish: "da",
  dutch: "nl",
  english: "en",
  esperanto: "eo",
  estonian: "et",
  filipino: "tl",
  finnish: "fi",
  french: "fr",
  frisian: "fy",
  galician: "gl",
  georgian: "ka",
  german: "de",
  greek: "el",
  gujarati: "gu",
  haitian: "ht",
  hausa: "ha",
  hawaiian: "haw",
  hebrew: "iw",
  hindi: "hi",
  hmong: "hmn",
  hungarian: "hu",
  icelandic: "is",
  igbo: "ig",
  indonesian: "id",
  irish: "ga",
  italian: "it",
  japanese: "ja",
  javanese: "jw",
  kannada: "kn",
  kazakh: "kk",
  khmer: "km",
  kinyarwanda: "rw",
  korean: "ko",
  kurdish: "ku",
  kyrgyz: "ky",
  lao: "lo",
  latin: "la",
  latvian: "lv",
  lithuanian: "lt",
  luxembourgish: "lb",
  macedonian: "mk",
  malagasy: "mg",
  malay: "ms",
  malayalam: "ml",
  maltese: "mt",
  maori: "mi",
  marathi: "mr",
  mongolian: "mn",
  myanmar: "my",
  nepali: "ne",
  norwegian: "no",
  nyanja: "ny",
  odia: "or",
  pashto: "ps",
  persian: "fa",
  polish: "pl",
  portuguese: "pt",
  punjabi: "pa",
  romanian: "ro",
  russian: "ru",
  samoan: "sm",
  scots: "gd",
  serbian: "sr",
  sesotho: "st",
  shona: "sn",
  sindhi: "sd",
  sinhala: "si",
  slovak: "sk",
  slovenian: "sl",
  somali: "so",
  spanish: "es",
  sundanese: "su",
  swahili: "sw",
  swedish: "sv",
  tajik: "tg",
  tamil: "ta",
  tatar: "tt",
  telugu: "te",
  thai: "th",
  turkish: "tr",
  turkmen: "tk",
  ukrainian: "uk",
  urdu: "ur",
  uyghur: "ug",
  uzbek: "uz",
  vietnamese: "vi",
  welsh: "cy",
  xhosa: "xh",
  yiddish: "yi",
  yoruba: "yo",
  zulu: "zu",
};

const emitGoogleTranslateStatus = (detail) => {
  window.dispatchEvent(new CustomEvent(GOOGLE_TRANSLATE_STATUS_EVENT, { detail }));
};

const setGoogleTranslatePendingState = (detail) => {
  sessionStorage.setItem(GOOGLE_TRANSLATE_PENDING_KEY, JSON.stringify(detail));
  emitGoogleTranslateStatus({ ...detail, isTranslating: true });
};

export const clearGoogleTranslatePendingState = () => {
  sessionStorage.removeItem(GOOGLE_TRANSLATE_PENDING_KEY);
  emitGoogleTranslateStatus({ isTranslating: false });
};

export const getGoogleTranslatePendingState = () => {
  const savedState = sessionStorage.getItem(GOOGLE_TRANSLATE_PENDING_KEY);
  if (!savedState) return null;

  try {
    return JSON.parse(savedState);
  } catch (error) {
    sessionStorage.removeItem(GOOGLE_TRANSLATE_PENDING_KEY);
    return null;
  }
};

const getGoogleTranslateContainer = () =>
  document.getElementById("google_translate_element");

const initializeGoogleTranslateElement = () => {
  const container = getGoogleTranslateContainer();

  if (!container || !window.google?.translate?.TranslateElement) {
    return false;
  }

  if (!container.children?.length) {
    new window.google.translate.TranslateElement(
      {
        pageLanguage: GOOGLE_TRANSLATE_PAGE_LANGUAGE,
        autoDisplay: false,
        layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
      },
      "google_translate_element"
    );
  }

  return true;
};

const setGoogleTranslateCookie = (languageCode) => {
  const cookieValue = `/${GOOGLE_TRANSLATE_PAGE_LANGUAGE}/${languageCode}`;
  document.cookie = `${GOOGLE_TRANSLATE_COOKIE_NAME}=${cookieValue};path=/`;
  if (
    window.location.hostname &&
    window.location.hostname !== "localhost" &&
    !window.location.hostname.match(/^\d+\.\d+\.\d+\.\d+$/)
  ) {
    document.cookie = `${GOOGLE_TRANSLATE_COOKIE_NAME}=${cookieValue};path=/;domain=${window.location.hostname}`;
  }
};

const getGoogleTranslateCookieDomains = () => {
  const hostname = window.location.hostname || "";
  if (!hostname || hostname === "localhost" || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    return [""];
  }

  const parts = hostname.split(".").filter(Boolean);
  const domains = new Set(["", hostname, `.${hostname}`]);

  if (parts.length > 2) {
    const parentDomain = parts.slice(-2).join(".");
    domains.add(parentDomain);
    domains.add(`.${parentDomain}`);
  }

  return Array.from(domains);
};

const clearGoogleTranslateCookie = () => {
  const expires = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  const paths = ["/", window.location.pathname || "/"];

  getGoogleTranslateCookieDomains().forEach((domain) => {
    paths.forEach((path) => {
      const domainPart = domain ? `;domain=${domain}` : "";
      document.cookie = `${GOOGLE_TRANSLATE_COOKIE_NAME}=;path=${path}${domainPart};${expires}`;
    });
  });

  document.body?.classList?.remove("translated-ltr", "translated-rtl");
  document.documentElement?.classList?.remove("translated-ltr", "translated-rtl");
};

const refreshPageForGoogleTranslate = () => {
  window.setTimeout(() => {
    window.location.reload();
  }, 150);
};

const waitForGoogleTranslateCombo = (attempt = 0) =>
  new Promise((resolve) => {
    initializeGoogleTranslateElement();
    const combo = document.querySelector(".goog-te-combo");
    if (combo || attempt >= 60) {
      resolve(combo);
      return;
    }

    window.setTimeout(async () => {
      resolve(await waitForGoogleTranslateCombo(attempt + 1));
    }, 250);
  });

export const normalizeGoogleTranslateCode = (language = {}) => {
  const rawCode = (language?.language_code || language?.code || "").trim();
  const rawName = (language?.language_name || language?.name || "").trim().toLowerCase();

  if (rawName && GOOGLE_LANGUAGE_CODE_MAP[rawName]) {
    return GOOGLE_LANGUAGE_CODE_MAP[rawName];
  }

  if (rawCode) {
    const normalizedCode = rawCode.replace("_", "-");
    const baseCode = normalizedCode.split("-")[0].toLowerCase();

    if (baseCode === "zh") {
      return "zh-CN";
    }

    if (baseCode.length === 2) {
      return baseCode;
    }

    if (baseCode.length === 3 && SUPPORTED_THREE_LETTER_CODES.has(baseCode)) {
      return baseCode;
    }
  }

  return GOOGLE_LANGUAGE_CODE_MAP[rawName] || "";
};

export const loadGoogleTranslateScript = () =>
  new Promise((resolve, reject) => {
    if (window.google?.translate?.TranslateElement) {
      initializeGoogleTranslateElement();
      resolve(window.google.translate);
      return;
    }

    window.googleTranslateElementInit = () => {
      initializeGoogleTranslateElement();
      resolve(window.google.translate);
    };

    const existingScript = document.getElementById(GOOGLE_TRANSLATE_SCRIPT_ID);
    if (existingScript) {
      if (window.google?.translate?.TranslateElement) {
        initializeGoogleTranslateElement();
        resolve(window.google.translate);
        return;
      }

      existingScript.addEventListener("load", () => {
        if (window.google?.translate?.TranslateElement) {
          initializeGoogleTranslateElement();
          resolve(window.google.translate);
        }
      });
      existingScript.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_TRANSLATE_SCRIPT_ID;
    script.src =
      "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    script.onload = () => {
      if (window.google?.translate?.TranslateElement) {
        initializeGoogleTranslateElement();
        resolve(window.google.translate);
      }
    };
    script.onerror = reject;
    document.body.appendChild(script);
  });

export const getStoredGoogleTranslateLanguage = () =>
  localStorage.getItem(GOOGLE_TRANSLATE_STORAGE_KEY) || GOOGLE_TRANSLATE_PAGE_LANGUAGE;

export const applyGoogleTranslateLanguage = async (languageCode) => {
  const selectedLanguage = languageCode || GOOGLE_TRANSLATE_PAGE_LANGUAGE;
  setGoogleTranslatePendingState({
    isTranslating: true,
    languageCode: selectedLanguage,
    startedAt: Date.now(),
  });

  localStorage.setItem(GOOGLE_TRANSLATE_STORAGE_KEY, selectedLanguage);

  if (selectedLanguage === GOOGLE_TRANSLATE_PAGE_LANGUAGE) {
    clearGoogleTranslateCookie();
    refreshPageForGoogleTranslate();
    return selectedLanguage;
  }

  setGoogleTranslateCookie(selectedLanguage);

  await loadGoogleTranslateScript();
  const combo = await waitForGoogleTranslateCombo();

  if (!combo) {
    refreshPageForGoogleTranslate();
    return selectedLanguage;
  }

  if (combo.value !== selectedLanguage) {
    combo.value = selectedLanguage;
    combo.dispatchEvent(new Event("change", { bubbles: true }));
  }

  return selectedLanguage;
};
